import { Picture } from '@ui';
import type { MouseEventHandler } from 'react';
import { forwardRef, useCallback, useMemo, useState } from 'react';
import API from '@api';
import ConfirmModal from '@components/Modals/ConfirmModal';
import ReviewProductModal from '@components/Modals/ReviewProductModal';
import { Link } from '@compat/router';
import ReviewAttachedFiles from '../components/ReviewAttachedFiles';
import ReviewControlBtns from '../components/ReviewControlBtns';
import ReviewReply from '../components/ReviewReply';
import {
  CommentsInstanceTypeEnum,
  CommentsTypeTypeEnum,
  StoreOrProductCommentsTypeEnum
} from '@schema/cart';
import type { CommentsItemsFragmentFragment } from '@schema/cart';
import { twcls } from '@utils/twcls';
import ClientReviewStats from '@components/ClientReviewStats';
import { observer } from 'mobx-react';
import PeopleReviewsModal from '@components/Modals/PeopleReviewsModal';
import dayjs from 'dayjs';
import { userNoAvatar, starYellow, starGreyRecipeRating } from '@images';

interface ProductReviewCardProps {
  message: CommentsItemsFragmentFragment;
  isBarMenu?: boolean;
  type?: 'productPage' | 'reviewsPage';
  noMarginLeft?: boolean;
  statsTooltipAlignRight?: boolean;
  onLinkClick?: MouseEventHandler<HTMLAnchorElement>;
  onEditOrDeleteReviewSubmit?: () => void;
}

const getDate = (date: string): string => dayjs(date).format('DD MMMM YYYY ');

type LeaveReviewItemInfoModalT = null | {
  text;
  attachedFiles;
  objectId: number;
  rating: number;
  commentType: CommentsTypeTypeEnum;
  availableForRefund?: any;
  productId?: any;
  answerUserId?: number | null | undefined;
  itemId?: any;
  price?: any;
};

const ProductReviewCard = observer(
  forwardRef<HTMLDivElement, ProductReviewCardProps>(
    (
      {
        message,
        onEditOrDeleteReviewSubmit,
        onLinkClick,
        isBarMenu,
        type = 'reviewsPage',
        noMarginLeft,
        statsTooltipAlignRight
      },
      ref
    ) => {
      const reviewDate = message?.date ? getDate(message?.date as string) : '';
      const peopleItem = message?.peopleItem;
      const isReview = message?.type === 'REVIEW';
      const isCanRateAnswer = !!message.isCanRateAnswer;
      const reviewId = +message.id;
      const answerText = message.answer;
      const answerRating = message?.answer_rating;
      const answerDate = message.date;
      const authorId = message?.people_id;
      const answerUserItem = message?.answerUserItem;
      const productId = message.catalogItem?.id ?? '';
      const isCanEdit = !!message.isCanEdit;
      const attachedFiles = useMemo(
        () => message?.photos || [],
        [message?.photos]
      );
      const text = message.text;
      const date = reviewDate;
      const rating = Number(message.rating);
      const productName =
        type === 'reviewsPage'
          ? message.catalogItem?.title || ''
          : message?.peopleItem?.fio || 'Пользователь';
      const productImg =
        type === 'reviewsPage'
          ? message.catalogItem?.fileItem?.cacheItem?.[0]?.filePath ||
            userNoAvatar
          : message?.peopleItem?.attachmentsItem?.cacheItem?.[0]?.filePath ||
            userNoAvatar;
      const [isConfirmModalOpened, setIsConfirmModalOpened] = useState(false);
      const [leaveReviewItemInfoModal, setLeaveReviewItemInfoModal] =
        useState<LeaveReviewItemInfoModalT>(null);

      const starsArr = useMemo(
        () => (rating ? Array.from(Array(rating).keys()) : []),
        [rating]
      );
      const greyStarsArr = useMemo(
        () => Array.from(Array(5 - rating).keys()),
        [rating]
      );

      const onCloseConfirmModal = useCallback(() => {
        setIsConfirmModalOpened(false);
      }, []);
      const onDeleteBtnClickHandler = useCallback(() => {
        setIsConfirmModalOpened(true);
      }, []);

      const onEditReviewClickHandler = useCallback(() => {
        setLeaveReviewItemInfoModal({
          text,
          attachedFiles,
          objectId: +reviewId,
          rating,
          commentType: isReview
            ? CommentsTypeTypeEnum.Review
            : CommentsTypeTypeEnum.Comment
        });
      }, [text, attachedFiles, reviewId, rating, isReview]);

      const onReviewProductModalClose = useCallback(() => {
        setLeaveReviewItemInfoModal(null);
      }, []);

      const [isPeopleReviewsModalOpened, setIsPeopleReviewsModalOpened] =
        useState(false);
      const onCloseModal = useCallback(() => {
        setIsPeopleReviewsModalOpened(false);
      }, []);
      const openModal = useCallback(() => {
        setIsPeopleReviewsModalOpened(true);
      }, []);

      const onDeleteReviewClickHandler = useCallback(() => {
        API.editPeopleReviewById({
          toDelete: true,
          data: {
            objectId: +reviewId,
            instance: CommentsInstanceTypeEnum.Catalog,
            commentType: CommentsTypeTypeEnum.Review
          }
        })
          .then(() => {
            onEditOrDeleteReviewSubmit?.();
          })
          .catch(error => {
            console.log('Error: ', error);
          })
          .finally(() => {
            setIsConfirmModalOpened(false);
          });
      }, [onEditOrDeleteReviewSubmit, reviewId]);

      const handleLinkClick: MouseEventHandler<HTMLAnchorElement> = useCallback(
        e => {
          if (e.ctrlKey || e.button === 1) {
            e.preventDefault();
            window.open(
              `https://www.delikateska.ru/product/${productId ?? ''}`
            );
            return;
          }
          onLinkClick?.(e);
        },
        [onLinkClick, productId]
      );

      const isAuthorChef =
        Number(authorId) === 1042957 || Number(authorId) === 22152;
      return (
        <>
          {isPeopleReviewsModalOpened && peopleItem && (
            <PeopleReviewsModal
              peopleItem={peopleItem}
              onCloseModal={onCloseModal}
            />
          )}
          <div
            ref={ref}
            data-testid='product-review-card'
            className={`bg-white py-8 relative border-0 border-b border-solid border-[#f1f1f1] w-full last-of-type:border-b-0 first-of-type:pt-0`}
          >
            <div className='flex items-center mb-3'>
              {type === 'productPage' ? (
                <div className='mr-5 w-12 h-12 flex items-center justify-center min-w-[3rem] rounded-[50%] overflow-hidden relative'>
                  <img
                    onClick={openModal}
                    className='block object-contain w-full h-full mx-auto cursor-pointer'
                    src={productImg}
                    alt={productName}
                  />
                </div>
              ) : (
                <Link
                  onClick={handleLinkClick}
                  to={`/product/${productId ?? ''}`}
                  className='group mr-5 w-12 h-12 flex items-center justify-center cursor-pointer min-w-[3rem] rounded-[50%] overflow-hidden relative'
                >
                  <img
                    className='block object-contain w-full h-full mx-auto'
                    src={productImg}
                    alt={productName}
                  />
                </Link>
              )}

              <div className='min-w-0'>
                {type === 'productPage' ? (
                  <div className='flex items-center mb-1 sm:items-start sm:flex-col sm:gap-y-1 gap-x-3'>
                    <div
                      onClick={openModal}
                      className={
                        'block min-w-0 font-medium text-base cursor-pointer break-words'
                      }
                    >
                      {productName}
                    </div>
                    {peopleItem && Number(peopleItem?.totalReviews) > 0 && (
                      <ClientReviewStats
                        openModal={openModal}
                        peopleItem={peopleItem}
                        withoutHover={isBarMenu}
                        isModalDisabled={isBarMenu}
                        tooltipAlignRight={statsTooltipAlignRight}
                      />
                    )}
                  </div>
                ) : (
                  <Link
                    onClick={handleLinkClick}
                    to={`/product/${productId ?? ''}`}
                    className='block min-w-0 font-medium text-base cursor-pointer break-words transition-colors hover:text-[var(--dark-red)]'
                  >
                    {productName}
                  </Link>
                )}

                <div className='flex items-center gap-x-4'>
                  <div className='text-[var(--ultra-dark-grey)] text-sm pt-0.5'>
                    {date}
                  </div>
                  {!isAuthorChef && isReview && (
                    <div className='flex items-center'>
                      {starsArr.map((i, ind) => (
                        <Picture
                          key={ind}
                          className='mr-1 last-of-type:mr-0'
                          icon={starYellow}
                          height='20'
                        />
                      ))}
                      {greyStarsArr.map((i, ind) => (
                        <Picture
                          key={ind}
                          className='mr-1 last-of-type:mr-0'
                          icon={starGreyRecipeRating}
                          height='20'
                        />
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className={twcls('ml-[4.375rem]', noMarginLeft && 'ml-0')}>
              <div className='text-sm leading-tight'>{text}</div>
              {attachedFiles?.length > 0 && (
                <ReviewAttachedFiles attachedFiles={attachedFiles} />
              )}
              {isCanEdit && (
                <ReviewControlBtns
                  answer={answerText}
                  openEditReviewModal={onEditReviewClickHandler}
                  onDeleteReviewClickHandler={onDeleteBtnClickHandler}
                />
              )}
              {answerText && (
                <ReviewReply
                  isBarMenu={isBarMenu}
                  reviewId={reviewId}
                  reviewType={StoreOrProductCommentsTypeEnum.ProductReview}
                  answer={answerText}
                  answerDate={answerDate}
                  messageUserId={authorId}
                  answerUserAvatar={
                    answerUserItem?.attachmentsItem?.cacheItem?.[0]?.filePath
                  }
                  answerUserFio={answerUserItem?.fio}
                  isCanRateAnswer={isCanRateAnswer}
                  answerRating={answerRating}
                />
              )}
            </div>

            {isConfirmModalOpened && (
              <ConfirmModal
                open={isConfirmModalOpened}
                title='Удалить отзыв?'
                onClose={onCloseConfirmModal}
                onConfirm={onDeleteReviewClickHandler}
              />
            )}
            {!!leaveReviewItemInfoModal && (
              <ReviewProductModal
                initialCurrentFormState={
                  leaveReviewItemInfoModal?.commentType?.toLowerCase() ===
                  'comment'
                    ? 1
                    : 0
                }
                isBackButton={
                  leaveReviewItemInfoModal?.commentType?.toLowerCase() !==
                  'comment'
                }
                onCloseModal={onReviewProductModalClose}
                isEditProductReview
                editText={leaveReviewItemInfoModal.text}
                editUploadedFiles={leaveReviewItemInfoModal.attachedFiles}
                editRating={leaveReviewItemInfoModal.rating}
                objectId={+leaveReviewItemInfoModal.objectId}
                availableForRefund={leaveReviewItemInfoModal.availableForRefund}
                itemId={leaveReviewItemInfoModal.productId}
                productId={leaveReviewItemInfoModal.itemId}
                itemPrice={leaveReviewItemInfoModal.price}
                commentType={
                  leaveReviewItemInfoModal.commentType ===
                  CommentsTypeTypeEnum.Review
                    ? 'Review'
                    : leaveReviewItemInfoModal.commentType
                }
                onEditOrDeleteReviewSubmit={onEditOrDeleteReviewSubmit}
                isVesovoi={message?.catalogItem?.vesovoi === 'Y'}
                // updateOrdersAfterSubmitReturnForm={updateOrdersAfterSubmitReturnForm}
              />
            )}
          </div>
        </>
      );
    }
  )
);

export default ProductReviewCard;
